import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { HelmetProvider } from 'react-helmet-async';
import { Fonts } from '@roots-ui/fonts';
import { SnackbarProvider } from '@roots-ui/snackbar';
import '@roots-ui/tokens/tokens.css';
import '@roots-ui/fonts/fonts.css';
import { QueryClientWrapper } from '@sagent/dara.apis-types.core';
import { daraRoutes } from './routes.js';

import { AuthProvider, Login, LogoutAction, PageWrapper, ProtectedComponent, DaraLayout, FeatureFlagProvider, ClientIdProvider, SharedRbac } from '@dara-core/shared';

declare const daraAppBuildNumber: string | undefined;

// This handler is used to handle errors during component loading errors due to stale build or other issues.
// It will reload the page up to 3 times before giving up.
window.addEventListener('vite:preloadError', (event) => {
  const retryCount = parseInt(localStorage.getItem('daraAppForceReloadCount') || '0', 10);
  const currentBuildNumber = localStorage.getItem('daraAppBuildNumber') || '';
  if (retryCount < 3) {
    localStorage.setItem('daraAppForceReloadCount', (retryCount + 1).toString());
    console.error(
      `currentBuildNumber: ${currentBuildNumber}, daraAppBuildNumber: ${daraAppBuildNumber} - Error loading component`,
      event
    );
    window.location.reload();
  } else {
    // Reset the count after reaching the max retries to avoid infinite reload loops
    localStorage.setItem('daraAppForceReloadCount', '0');
  }
});

const router = createBrowserRouter([
  {
    ErrorBoundary: () => {
      throw useRouteError();
    },
    element: <AuthProvider />,
    children: [
      {
        path: '/',
        element: <Login />,
      },
      {
        path: '/logout',
        element: <LogoutAction />,
      },
      {
        element: (
          <SharedRbac.RBACProvider>
            <ProtectedComponent component={DaraLayout} />
          </SharedRbac.RBACProvider>
        ),
        children: daraRoutes,
      },
    ],
  },
]);

export function DaraAppShell() {
  // Store the build number in localStorage for later use
  useEffect(() => {
    if (daraAppBuildNumber) {
      localStorage.setItem('daraAppBuildNumber', daraAppBuildNumber);
    }
  }, [daraAppBuildNumber]);

  return (
    <HelmetProvider>
      <QueryClientWrapper>
        <FeatureFlagProvider>
          <PageWrapper hasOutlet>
            <RecoilRoot>
              <ClientIdProvider>
                <SnackbarProvider>
                  <Fonts />
                  <RouterProvider router={router} />
                </SnackbarProvider>
              </ClientIdProvider>
            </RecoilRoot>
          </PageWrapper>
        </FeatureFlagProvider>
      </QueryClientWrapper>
    </HelmetProvider>
  );
}
